import { createAsyncThunk, createSlice } from "@reduxjs/toolkit";
import { toast } from "react-toastify";

/**
 * A cart line this app can still render and order.
 *
 * Carts saved by the pre-v2 build carry `foodImage: [{extraLarge:{imageUrl}}]`
 * and no `image`, so they render without a picture — but the real problem is
 * that checkout posts `foodId: item._id` and those ids don't exist in the v2
 * catalogue, so such a line can only ever fail at order time. Dropping them is
 * kinder than letting the customer discover it at checkout.
 */
const isUsableCartLine = (item) =>
  !!item &&
  typeof item._id === "string" &&
  /^[0-9a-fA-F]{24}$/.test(item._id) &&
  Number.isFinite(Number(item.price)) &&
  Number(item.foodQty) > 0 &&
  // Unmistakably a v1 line.
  item.foodImage === undefined;

// Guarded: a malformed `cartList` would otherwise throw while this module is
// still evaluating, taking the whole app down before it renders.
const loadCartList = () => {
  try {
    const stored = JSON.parse(localStorage.getItem("cartList"));
    if (!Array.isArray(stored)) return [];

    const usable = stored.filter(isUsableCartLine);
    if (usable.length !== stored.length) {
      localStorage.setItem("cartList", JSON.stringify(usable));
    }
    return usable;
  } catch {
    return [];
  }
};

const initialState = {
  cartItems: loadCartList(),
};
/**
 * How many of this dish the kitchen actually has. A catalogue food carries
 * `stock` (normalized to `foodStock`); a cart line keeps the `foodStock` it was
 * added with. `foodQty` is the quantity *in the cart* and must never be read as
 * stock — doing so used to overwrite the real stock with `undefined`, which
 * silently disabled the maximum-quantity check.
 */
const stockOf = (item) => {
  const stock = Number(item?.foodStock ?? item?.stock);
  return Number.isFinite(stock) ? stock : 0;
};

export const addItem = createAsyncThunk(
  "cart/addItem",
  async (newItem, { getState }) => {
    const { cartItems } = getState().cart;
    const existingItemIndex = cartItems.findIndex(
      (item) => item._id === newItem._id
    );

    if (existingItemIndex !== -1) {
      const existingItem = cartItems[existingItemIndex];
      const foodStock = stockOf(existingItem);
      let requestQty = existingItem.foodQty + 1;

      if (foodStock > 0 && requestQty > foodStock) {
        requestQty = foodStock;
        toast.warning("You have reached maximum food quantity.");
        // Already at the cap — nothing to update.
        if (requestQty === existingItem.foodQty) return null;
      }

      return {
        index: existingItemIndex,
        item: { ...existingItem, foodQty: requestQty },
      };
    }

    const foodStock = stockOf(newItem);
    if (foodStock <= 0) {
      toast.warning("This item is out of stock.");
      return null;
    }

    return {
      index: -1,
      item: { ...newItem, foodQty: 1, foodStock },
    };
  }
);
const cartSlice = createSlice({
  name: "cart",
  initialState,
  reducers: {
    addItemLocally: (state, action) => {
      state.cartItems.push({ ...action.payload, foodQty: 1 });
    },
    setCartList: (state, action) => {
      state.cartItems = action.payload;
    },
    removeAllItems: (state) => {
      state.cartItems = [];
    },
    removeItem: (state, action) => {
      const itemId = action.payload;
      let removeData = state.cartItems.filter((item) => item._id !== itemId);
      state.cartItems = removeData;
    },

    addAdditionalInfo: (state, action) => {
      const { additionalInfo, id } = action.payload;
      const existingItem = state.cartItems.find((item) => item._id === id);
      if (existingItem) {
        existingItem.additionalInfo = additionalInfo;
      } else {
      }
    },

    decreaseItem: (state, action) => {
      const itemId = action.payload._id;
      const existingItemIndex = state.cartItems.findIndex(
        (item) => item._id === itemId
      );
      const existingItem = state.cartItems.find((item) => item._id === itemId);
      if (existingItem) {
        if (existingItem.foodQty > 1) {
          existingItem.foodQty--;
        } else {
          state.cartItems.splice(existingItemIndex, 1);
        }
      }
    },
  },
  extraReducers: (builder) => {
    builder.addCase(addItem.fulfilled, (state, action) => {
      // `null` means the add was declined (out of stock, or already at the
      // maximum) — the thunk used to return undefined here, which threw.
      if (!action.payload) return;

      const { index, item } = action.payload;
      if (index !== -1) {
        state.cartItems[index] = item;
      } else {
        state.cartItems.push(item);
      }
    });
  },
});
export const {
  removeItem,
  setCartList,
  removeAllItems,
  addItemLocally,
  decreaseItem,
  addAdditionalInfo,
} = cartSlice.actions;
export default cartSlice.reducer;
